import React, { useEffect, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { BottomNav } from "@/components/BottomNav";
import { useI18n } from "@/i18n";
import { useAppContext } from "@/state/AppContext";
import { useRouter } from "@/router/Router";
import { fetchAddresses, fetchLoyalty, fetchOrders, fetchTaste, type SavedAddress } from "@/lib/api";
import { MarketingToggle, ReferralCard } from "@/components/MarketingSection";
import { SubscriptionsList } from "@/components/Subscriptions";
import { useConfig } from "@/lib/useConfig";
import { BirthdayRow } from "@/components/BirthdaySheet";
import { openExternalLink } from "@/lib/telegram";
import type { Locale, LoyaltyState, Order } from "@/types";
import { useCart } from "@/state/CartContext";
import { useCatalog } from "@/state/CatalogContext";

const svg = (d: string) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" dangerouslySetInnerHTML={{ __html: d }} />
);
const IC = {
  orders: svg('<path d="M6 7h12l-1 13H7L6 7zM9 7a3 3 0 0 1 6 0"/>'),
  fav: svg('<path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z"/>'),
  addr: svg('<path d="M12 21s-6-5.7-6-11a6 6 0 0 1 12 0c0 5.3-6 11-6 11z"/><circle cx="12" cy="10" r="2.2"/>'),
  biz: svg('<path d="M5 11h11v3a5 5 0 0 1-5 5h-1a5 5 0 0 1-5-5zM16 12h1.5a2.5 2.5 0 0 1 0 5H15M8 4v4M11 3v5M14 4v4"/>'),
  passport: svg('<rect x="5" y="3.5" width="14" height="17" rx="2"/><circle cx="12" cy="11" r="3"/><path d="M9 17h6"/>'),
  taste: svg('<path d="M12 3c4 4 6 7 6 10a6 6 0 0 1-12 0c0-3 2-6 6-10z"/>'),
  recipes: svg('<path d="M5 11h11v3a5 5 0 0 1-5 5h-1a5 5 0 0 1-5-5zM16 12h1.5a2.5 2.5 0 0 1 0 5H15M8 4v4M11 3v5M14 4v4"/>'),
  bday: svg('<path d="M4 20h16M5 20v-7h14v7M8 13v-2M12 13v-2M16 13v-2M8 8.5a1 1 0 0 0 0-2M12 8.5a1 1 0 0 0 0-2M16 8.5a1 1 0 0 0 0-2"/>'),
  events: svg('<rect x="4" y="6" width="16" height="14" rx="2"/><path d="M4 10h16M9 3v4M15 3v4"/>'),
  lang: svg('<circle cx="12" cy="12" r="8.5"/><path d="M3.5 12h17M12 3.5c2.5 2.5 3.5 5.5 3.5 8.5s-1 6-3.5 8.5c-2.5-2.5-3.5-5.5-3.5-8.5s1-6 3.5-8.5z"/>'),
  journal: svg('<path d="M5 4h11l3 3v13H5zM9 9h7M9 13h7M9 17h4"/>'),
  bell: svg('<path d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4zM10 20a2 2 0 0 0 4 0"/>'),
  faq: svg('<circle cx="12" cy="12" r="8.5"/><path d="M9.6 9.6a2.4 2.4 0 1 1 3.4 2.2c-.6.3-1 .9-1 1.6v.4M12 17h.01"/>'),
  chat: svg('<path d="M5 5h14v10H9l-4 4z"/>'),
  arrow: svg('<path d="M9 6l6 6-6 6"/>'),
};

/** A row inside a profile group: icon, label, optional value on the right, chevron. */
function Row({ icon, label, value, onClick }: { icon: React.ReactNode; label: string; value?: string | null; onClick: () => void }) {
  return (
    <button className="pf-row" onClick={onClick}>
      <span className="pf-ic">{icon}</span>
      <span className="pf-row__label">{label}</span>
      {value ? <span className="pf-row__val">{value}</span> : null}
      <span className="pf-row__chev" aria-hidden="true">›</span>
    </button>
  );
}

/** Top block row (orders, favourites, addresses): filled brand icon, hint and a counter. */
function ShopRow({ icon, label, hint, count, onClick }: { icon: React.ReactNode; label: string; hint: string; count: number; onClick: () => void }) {
  return (
    <button className="pf-shop__row" onClick={onClick}>
      <span className="pf-shop__ic">{icon}</span>
      <span className="pf-shop__text">
        <b>{label}</b>
        <small>{hint}</small>
      </span>
      {count > 0 && <span className="pf-shop__count">{count}</span>}
    </button>
  );
}

const LOCALE_LABEL: Record<Locale, string> = { ru: "Русский", uz: "O'zbekcha", en: "English" };
const ACTIVE: Order["status"][] = ["new", "confirmed", "paid", "preparing", "ready_for_pickup", "handed_to_courier"];
const REWARD_EVERY = 10;

/** "Ещё 7 заказов" with Russian plural forms; uz/en use one form. */
function ordersLeft(n: number, locale: Locale, t: (k: string, v?: Record<string, string | number>) => string): string {
  if (locale === "en") return t(n === 1 ? "profile.loyaltyLeft_one" : "profile.loyaltyLeft", { count: n });
  if (locale !== "ru") return t("profile.loyaltyLeft", { count: n });
  const m10 = n % 10, m100 = n % 100;
  const form = m10 === 1 && m100 !== 11 ? "one" : m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14) ? "few" : "many";
  return t(`profile.loyaltyLeft_${form}`, { count: n });
}

export function ProfilePage() {
  const { features, botUsername } = useConfig();
  const { t, locale, setLocale } = useI18n();
  const { user } = useAppContext();
  const { navigate } = useRouter();
  const { favorites } = useCart();
  const { products } = useCatalog();
  const [loyalty, setLoyalty] = useState<LoyaltyState | null>(null);
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [addresses, setAddresses] = useState<SavedAddress[] | null>(null);
  const [archetype, setArchetype] = useState<string | null>(null);

  useEffect(() => {
    fetchLoyalty().then(setLoyalty).catch(() => setLoyalty(null));
    fetchOrders().then(setOrders).catch(() => setOrders([]));
    fetchAddresses().then(setAddresses).catch(() => setAddresses([]));
    fetchTaste()
      .then((p) => setArchetype(p.archetype ? t(`taste.archetypes.${p.archetype}.name`) : null))
      .catch(() => setArchetype(null));
  }, [locale]);

  const cycleLocale = () => {
    const order: Locale[] = ["ru", "uz", "en"];
    setLocale(order[(order.indexOf(locale) + 1) % order.length]);
  };

  const name = user?.firstName ?? t("profile.guest");
  // Loyalty: diamonds filled for orders made towards the next reward.
  const done = loyalty ? (loyalty.hasRewardAvailable ? REWARD_EVERY : Math.max(0, REWARD_EVERY - loyalty.ordersUntilReward)) : 0;
  const active = (orders ?? []).filter((o) => ACTIVE.includes(o.status));
  const last = orders?.[0];
  const favIds = favorites ?? [];
  const firstFav = products.find((p) => favIds.includes(p.id));
  const ordersHint = !orders ? "…" : last ? t("profile.ordersLast", { status: t(`orders.status.${last.status}`) }) : t("profile.ordersNone");
  const favHint = favIds.length === 0 ? t("profile.favNone") : firstFav ? (favIds.length > 1 ? t("profile.favSome", { name: firstFav.name, count: favIds.length - 1 }) : firstFav.name) : t("profile.favCount", { count: favIds.length });
  const addrHint = !addresses ? "…" : addresses.length ? addresses.map((a) => a.label).slice(0, 3).join(", ") : t("profile.addrNone");

  return (
    <>
      <TopBar title={t("profile.title")} showCart={false} />
      <main className="page">
        {/* 1. Customer card + loyalty */}
        <section className="pf-hero">
          <div className="pf-top">
            <div className="pf-ava">{name.charAt(0).toUpperCase()}</div>
            <div style={{ minWidth: 0 }}>
              <div className="pf-name">{name}</div>
              <div className="pf-sub">{user?.username ? `@${user.username}` : t("profile.noPhone")}</div>
            </div>
          </div>
          {loyalty && (
            <div className="pf-loy">
              <div className="pf-loy__head">
                <span>{t("profile.loyaltyTitle")}</span>
                <b>
                  {done} / {REWARD_EVERY}
                </b>
              </div>
              <div className="pf-dia" aria-hidden="true">
                {Array.from({ length: REWARD_EVERY }, (_, i) => (
                  <i key={i} className={i < done ? "on" : ""} />
                ))}
              </div>
              <div className="pf-loy__note">{loyalty.hasRewardAvailable ? t("home.loyaltyReady") : ordersLeft(loyalty.ordersUntilReward, locale, t)}</div>
            </div>
          )}
        </section>

        {features.referrals && <ReferralCard />}
        {features.subscriptions && <SubscriptionsList />}

        {/* 2. Orders, favourites, addresses */}
        <div className="pf-shop">
          <ShopRow icon={IC.orders} label={t("profile.orders")} hint={ordersHint} count={active.length} onClick={() => navigate({ name: "orders" })} />
          <ShopRow icon={IC.fav} label={t("profile.favorites")} hint={favHint} count={favIds.length} onClick={() => navigate({ name: "favorites" })} />
          <ShopRow icon={IC.addr} label={t("profile.addresses")} hint={addrHint} count={addresses?.length ?? 0} onClick={() => navigate({ name: "addresses" })} />
        </div>

        {/* 3. For business (compact here; the big exhibition banner stays on Home) */}
        <button className="pf-biz" onClick={() => navigate({ name: "lead", source: "profile" })}>
          <span className="pf-ic pf-ic--surface">{IC.biz}</span>
          <span className="pf-biz__text">
            <b>{t("profile.b2b")}</b>
            <small>{t("lead.ctaShort")}</small>
          </span>
          <span className="pf-biz__go">{IC.arrow}</span>
        </button>

        {/* 4. Coffee & taste */}
        <div className="pf-label">{t("profile.groupTaste")}</div>
        <div className="pf-group">
          {features.passport && <Row icon={IC.passport} label={t("profile.passport")} onClick={() => navigate({ name: "passport" })} />}
          <Row icon={IC.taste} label={t("profile.taste")} value={archetype} onClick={() => navigate({ name: "taste" })} />
          <Row icon={IC.recipes} label={t("profile.recipes")} onClick={() => navigate({ name: "recipes" })} />
          {features.birthday && <BirthdayRow variant="row" icon={IC.bday} />}
          {features.events && <Row icon={IC.events} label={t("profile.events")} onClick={() => navigate({ name: "events" })} />}
        </div>

        {/* 5. Settings */}
        <div className="pf-label">{t("profile.groupSettings")}</div>
        <div className="pf-group">
          <Row icon={IC.lang} label={t("profile.language")} value={LOCALE_LABEL[locale]} onClick={cycleLocale} />
          <Row icon={IC.journal} label={t("profile.journal")} onClick={() => navigate({ name: "posts" })} />
          <MarketingToggle variant="row" icon={IC.bell} />
        </div>

        {/* 6. Help */}
        <div className="pf-label">{t("profile.groupHelp")}</div>
        <div className="pf-group">
          <Row icon={IC.faq} label={t("faq.title")} onClick={() => navigate({ name: "faq" })} />
          {botUsername && <Row icon={IC.chat} label={t("faq.writeUs")} onClick={() => openExternalLink(`https://t.me/${botUsername}`)} />}
        </div>

        {/* 7. Documents */}
        <div className="pf-foot">
          <button className="link-button" onClick={() => navigate({ name: "post", slug: "offer" })}>
            {t("profile.offer")}
          </button>
          <span aria-hidden="true"> · </span>
          <button className="link-button" onClick={() => navigate({ name: "post", slug: "privacy" })}>
            {t("profile.privacy")}
          </button>
          <div>KMS ROASTING · {t("profile.city")}</div>
        </div>
      </main>
      <BottomNav />
    </>
  );
}
